import styled from '@emotion/styled';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { navigationService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { Button } from 'src/shared/ds/components/Button';
import { ButtonGroup } from 'src/shared/ds/components/ButtonGroup';
import { Http } from 'src/shared/services/Http';
import { color, font, mediaQueries } from 'src/shared/styles/variables';
import leftMountains from 'src/shared/svg/welcome-magic-link/left-mountains.svg';
import rightMountains1 from 'src/shared/svg/welcome-magic-link/right-mountains1.svg';
import rightMountains2 from 'src/shared/svg/welcome-magic-link/right-mountains2.svg';
import stars from 'src/shared/svg/welcome-magic-link/sparkling-stars.svg';

const Container = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
});
const LeftMountains = styled.div({
    bottom: 0,
    left: 0,
    position: 'absolute',
    [mediaQueries.md]: {
        display: 'none',
    },
});
const RightMountain = styled.div({
    textAlign: 'right',
    bottom: 0,
    right: 0,
    position: 'absolute',
});
const Stars = styled.div({
    textAlign: 'center',
    position: 'absolute',
});
const ContentContainer = styled.div({
    position: 'relative',
    bottom: 244,
});
const Title = styled.h1(
    {
        margin: 0,
        color: color.pickledBluewood,
        paddingBottom: '12px',
        maxWidth: '700px',
    },
    font.heavy(font.size.header),
);
export const Subtitle = styled.h3(
    {
        color: color.blueBayoux,
        lineHeight: 1.8,
        paddingBottom: '40px',
    },
    font.book(font.size.basePlus),
);
const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});
const ButtonsRow = styled.div({
    display: 'flex',
    justifyContent: 'center',
});
interface ButtonsContainerProps {
    singleButton: boolean;
}
const ButtonsContainer = styled.div<ButtonsContainerProps>(({ singleButton }) => {
    return {
        width: `${singleButton ? '240px' : '520px'} !important`,
        display: singleButton ? 'flex' : 'block',
    };
});
interface Response {
    firstName: string;
    lastName: string;
    email: string;
    birthDate: string;
    dateOfBirth: string;
    token: string;
    uidb64: string;
    companyCode: string;
    ssoEnabled: boolean;
}

export const RegistrationPage: React.FC = () => {
    const params = queryString.parse(window.location.search);
    const dispatch = useDispatch<Dispatch>();
    const { t } = useTranslation();
    const location = useLocation<Response>();
    const user = useSelector(getUserInformation);
    const [firstName, setFirstName] = useState(location.state?.firstName || '');
    const [lastName, setLastName] = useState(location.state?.lastName || '');
    const [email, setEmail] = useState(location.state?.email || '');
    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [birthDate, setBirthDate] = useState(
        location.state?.dateOfBirth || location.state?.birthDate || '',
    );
    const [companyCode, setCompanyCode] = useState(location.state?.companyCode || '');
    const [shortLengthToken, setShortLengthToken] = useState('');
    // This page is also used for Dependent Registration, using invite uuid to get the dependent data
    const isDependentInvitationFlow = location.pathname.includes('dependent-invitation') || false;
    const uidb64 = params.uidb64 || undefined;
    const token = params.token || undefined;
    const isSSOFlow = user.registrationSource === 'Single Sign On' || false;

    const updateUserInformation = async () => {
        // We need to update the member data after passing the SSO Flow to show this page correctly
        if (firstName === '') {
            await dispatch.user.fetchUserInfo();
            setFirstName(user.firstName);
        }
    };

    const getStarted = () => {
        if (isSSOFlow) {
            navigationService.navigate('/preferred-email');
        } else {
            navigationService.navigate('/consent-form-page', {
                token: shortLengthToken,
                companyCode,
                firstName,
                lastName,
                email,
                birthDate,
                uidb64,
            });
        }
    };

    const leftButtonLabel = params.token
        ? t('auth:registration_page.button.sign_up_with_single_sign_on')
        : t('auth:login_form.back');

    const redirectLeftButton = () => {
        if (params.token) {
            navigationService.navigate('/single-sign-on');
        } else {
            navigationService.navigate('/registration-basics', {
                firstName,
                lastName,
                birthDate,
                email,
                companyCode,
            });
        }
    };

    const hitTokenApiEndpoint = async () => {
        const http: Http = new Http();
        const pk = token || uidb64 || '';
        const url = `accounts/check-signuptoken/${pk}`;

        if (!isSSOFlow && pk) {
            try {
                const response = await http.GET_UNAUTHENTICATED<Response>(url);

                setFirstName(response.firstName);
                setLastName(response.lastName || '');
                setEmail(response.email || '');
                setBirthDate(response.dateOfBirth || '');
                setShortLengthToken(response.token);
                setCompanyCode(response.companyCode);
                setSsoEnabled(response.ssoEnabled);
                setLoading(false);
            } catch (err) {
                const detail = err?.body?.detail;
                switch (detail) {
                    case 'Token expired':
                        navigationService.navigate('/invalid-registration-token', {
                            token: params.token,
                        });
                        break;
                    case 'Member has already registered an account':
                        navigationService.navigate('/login', { existingAccount: true });
                        break;
                    case 'Member already has an account':
                        navigationService.navigate('/login', { existingAccount: true });
                        break;
                    case 'Person ineligible':
                        navigationService.navigate('/ineligible');
                        break;
                    default:
                        break;
                }
            }
        } else {
            await updateUserInformation();
            setLoading(false);
        }
    };

    useEffect(() => {
        hitTokenApiEndpoint();
    }, []);

    return (
        <>
            {loading ? (
                <CenteredSpinner />
            ) : (
                <>
                    <BrandImage
                        src={logoImg}
                        onClick={() => {
                            window.location.href = 'https://www.modernhealth.com';
                        }}
                    />
                    <Container>
                        <LeftMountains>
                            <img src={leftMountains} />
                        </LeftMountains>
                        <RightMountain>
                            <img src={rightMountains1} />
                        </RightMountain>
                        <RightMountain>
                            <img src={rightMountains2} />
                        </RightMountain>
                        <Stars>
                            <img src={stars} />
                            <ContentContainer>
                                <Title>{t('auth:registration_page.title', { firstName })}</Title>
                                <Subtitle>{t('auth:registration_page.description')}</Subtitle>
                                <ButtonsRow>
                                    <ButtonsContainer
                                        singleButton={
                                            isDependentInvitationFlow ||
                                            isSSOFlow ||
                                            (!!token && !ssoEnabled)
                                        }
                                    >
                                        {isDependentInvitationFlow ||
                                        isSSOFlow ||
                                        (token && !ssoEnabled) ? (
                                            <Button
                                                onClick={getStarted}
                                                title={t(
                                                    'auth:registration_page.button.get_started',
                                                )}
                                                variant="primary"
                                                size="large"
                                                fullWidth
                                            />
                                        ) : (
                                            <>
                                                <ButtonGroup
                                                    onFirstButtonClick={redirectLeftButton}
                                                    firstButtonText={leftButtonLabel}
                                                    onSecondButtonClick={getStarted}
                                                    secondButtonText={t(
                                                        'auth:registration_page.button.get_started',
                                                    )}
                                                />
                                            </>
                                        )}
                                    </ButtonsContainer>
                                </ButtonsRow>
                            </ContentContainer>
                        </Stars>
                    </Container>
                </>
            )}
        </>
    );
};
